/**
 * Search drawer Zustand store
 *
 * Mirrors the selectedUser data from the Redux search state.
 * Synced via side-effect in the search Redux middleware — the single
 * source of truth remains Redux until the migration completes.
 *
 * Intended as the first step in a gradual Redux-to-Zestand migration:
 * new features should read from this store instead of Redux selectors,
 * and over time more slices can migrate here.
 *
 * DATA FLOW:
 *
 *   Redux → Zustand (hydration):
 *     1. User opens profile drawer → Redux sets selectedUser/currentConstituent
 *     2. useEffect in VolunteerRolesSection calls SearchDrawerActions.setSelectedUser()
 *     3. Zustand is now hydrated with the Redux data
 *
 *   Zustand → Redux (sync after mutations):
 *     1. TanStack mutation onSuccess calls SearchDrawerActions.setSelectedUser()
 *        with fresh API response
 *     2. sync.ts subscription detects the change (reference inequality),
 *        dispatches receiveCurrentConstituent() back to Redux
 *     3. Redux selectedUser stays fresh for legacy components
 *
 *   Circular update guard: the search middleware writes the same object
 *   reference back to Zustand, so the subscription sees no change and stops.
 *
 * @see ./sync.ts for the Zustand→Redux subscription
 * @see apps/frontend/src/components/search/ducks/middleware.js (lines 374-381) for Redux→Zustand middleware sync
 */

import type { Constituent, User } from "@/types.d.ts"
import { defineZustandStore } from "@/zustand-utils"

type SearchDrawerState = {
  selectedUser: User | Constituent | null
}

const initialState: SearchDrawerState = {
  selectedUser: null,
}

export const {
  store: searchDrawerStore,
  set,
  useStoreWithSelector: useSearchDrawerStore,
} = defineZustandStore({
  initialState,
  storeName: "SearchDrawer",
})

export const SearchDrawerActions = {
  setSelectedUser: (user: User | Constituent | null) => set({ selectedUser: user }),
  clearSelectedUser: () => set({ ...initialState }),
} as const
